<template>
    <div>
        <input type="hidden" name="tier_base" :value="tierBase">

        <!-- Chaque palier est une remise sur le prix public : le prix remisé n'est qu'une
             autre façon de la saisir, et seul le taux est enregistré. -->
        <p class="ix-text-small text-muted">
            Chaque palier accorde une remise sur le prix tarif de {{ currency(public()) }} : la ligne de devis
            garde le prix tarif et porte la remise. Saisissez la remise ou le prix remisé, l'autre suit.
        </p>

        <!-- Sans grille de remises, ou sans profil à faire entrer dans le calcul (société
             sans profil sur une règle client), le réglage n'aurait aucun effet : une ligne
             discrète le dit à sa place quand il est enregistré. -->
        <div class="form-group" v-if="gridEnabled && profiles.length">
            <div class="custom-control custom-switch d-inline-block">
                <input type="checkbox" class="custom-control-input" id="tier_base_toggle" v-model="cumulates">
                <label class="custom-control-label" for="tier_base_toggle">
                    Maintenir la remise par profil client
                </label>
            </div>
            <!-- Hors du label : un clic sur l'aide ne doit pas basculer l'interrupteur. -->
            <i class="far fa-question-circle ix-text-tertiary ml-1" v-b-tooltip="{ title: cumulHelp, trigger: 'hover', placement: 'right', customClass: 'ix-line-tooltip' }"></i>
        </div>
        <p class="ix-text-small text-muted" v-else-if="tierBase == BASE_DISCOUNTED">
            <i class="far fa-info-circle"></i> {{ cumulUnavailable }}
        </p>

        <div class="ix-table-box mb-3">
            <!-- Les cellules par profil font plusieurs lignes : sans alignement centré, les
                 champs de saisie se colleraient en haut de la ligne. Sans elles, la note sous
                 le prix remisé ne doit pas décentrer les autres champs de la ligne. -->
            <table class="ix-table" :class="{ 'ix-table-align-middle': showsProfiles }">
                <thead>
                    <tr>
                        <!-- L'ordre suit le calcul, de gauche à droite : le palier remise le prix
                             public, puis la remise du profil s'applique sur ce prix remisé. -->
                        <th style="width: 140px;">Palier</th>
                        <th style="width: 150px;">Remise</th>
                        <th style="width: 170px;">Prix remisé</th>

                        <template v-if="showsProfiles">
                            <th style="width: 140px;">Profil</th>
                            <th class="text-right" style="width: 150px;">Remise appliquée</th>
                            <th class="text-right" style="width: 150px;">Prix net</th>
                        </template>

                        <th>Commentaire</th>
                        <th style="width: 60px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <!-- Ce qui se passe sous le premier seuil, dit à sa place dans la table
                         plutôt que dans un bandeau : c'est la ligne qu'on lit en premier. -->
                    <tr v-if="firstThreshold > 1" class="ix-tier-fallback">
                        <td :colspan="columnCount">
                            <i class="far fa-level-down-alt fa-flip-horizontal mr-1"></i>
                            Moins de {{ quantity(firstThreshold) }} : cette règle ne s'applique pas.
                        </td>
                    </tr>
                    <tr v-for="(tier, index) in tiers" :key="tier.uniq">
                        <td>
                            <input type="number" class="form-control form-control-sm text-right"
                                   :class="{ 'is-invalid': isDuplicate(tier) }"
                                   :name="'tiers[' + index + '][from_quantity]'"
                                   v-model="tier.from_quantity" min="1" step="0.001" required>
                        </td>
                        <td>
                            <div class="input-group input-group-sm">
                                <input type="number" class="form-control text-right"
                                       :name="'tiers[' + index + '][value]'"
                                       :value="tier.value" @input="onRateInput(tier, $event.target.value)"
                                       min="0" max="100" step="0.001" required>
                                <div class="input-group-append">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                        </td>
                        <td>
                            <!-- Jamais envoyé : le serveur n'enregistre que le taux. -->
                            <div class="input-group input-group-sm">
                                <input type="number" class="form-control text-right"
                                       :value="tier.price" @input="onPriceInput(tier, $event.target.value)"
                                       @blur="syncPrice(tier)"
                                       :disabled="public() <= 0" min="0" :max="public()" step="0.01">
                                <div class="input-group-append">
                                    <span class="input-group-text">€</span>
                                </div>
                            </div>
                            <div class="ix-tier-beaten-note" v-if="!showsProfiles && beatenBy(tier).length">{{ beatenNote(tier) }}</div>
                        </td>
                        <template v-if="showsProfiles">
                            <td>
                                <div class="ix-tier-profile" v-for="profile in profiles" :key="profile.code || 'none'">
                                    <span class="ix-tier-profile-code">
                                        <span class="ix-badge grey" v-if="!profile.no_profile">{{ profile.code }}</span>
                                        <span class="text-muted" v-else>-</span>
                                    </span>
                                    <span class="ix-text-secondary ml-1" v-if="profile.rate !== null">
                                        {{ rate(profile.rate) }}
                                    </span>
                                    <span class="text-muted ml-1" v-else-if="profile.no_profile">-</span>
                                    <span class="text-muted ml-1" v-else
                                          title="Aucun taux pour ce profil sur le code de remise de l'article.">
                                        pas de taux
                                    </span>
                                </div>
                            </td>
                            <td class="text-right">
                                <div class="ix-tier-profile" v-for="profile in profiles" :key="profile.code || 'none'">
                                    {{ rate(cumulatedRate(tier, profile)) }}
                                </div>
                            </td>
                            <td class="text-right">
                                <div class="ix-tier-profile ix-text-bold" v-for="profile in profiles" :key="profile.code || 'none'"
                                     :class="isBeaten(profileNet(tier, profile), profile) ? beatenClass : null"
                                     :title="isBeaten(profileNet(tier, profile), profile) ? beatenTitle(profile) : null">
                                    {{ currency(profileNet(tier, profile)) }}
                                </div>
                            </td>
                        </template>
                        <td>
                            <input type="text" class="form-control form-control-sm" maxlength="255"
                                   :name="'tiers[' + index + '][comment]'"
                                   v-model="tier.comment" placeholder="ex. palier haut">
                        </td>
                        <td class="text-center">
                            <span class="btn btn-sm transparent" @click="removeTier(index)">
                                <i class="far fa-trash"></i>
                            </span>
                        </td>
                    </tr>
                    <tr v-if="!tiers.length">
                        <td :colspan="columnCount" class="text-center text-muted">
                            Aucun palier : la règle est sans effet tant qu'elle n'en porte pas.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Le champ fautif est déjà en rouge : une ligne suffit à dire pourquoi. -->
        <p class="ix-text-small text-danger mt-2 mb-0" v-if="duplicates.length">
            Deux paliers ne peuvent pas avoir le même seuil ({{ duplicates.join(', ') }}).
        </p>

        <button type="button" class="ix-btn link" @click="addTier"><i class="far fa-plus"></i> Ajouter un palier</button>

        <!-- Réglages secondaires : sous les paliers, qui sont l'essentiel de la règle. -->
        <div class="form-row mt-3">
            <div class="form-group col-md-4">
                <label for="ends_on">Fin de validité</label>
                <input type="date" class="form-control form-control-sm" id="ends_on" name="ends_on" v-model="endsOn">
                <small class="form-text text-muted">
                    Facultative. Passée cette date, la règle n'est plus appliquée mais reste
                    consultable.
                </small>
            </div>

            <div class="form-group col-md-4">
                <label for="comment">Commentaire</label>
                <input type="text" class="form-control form-control-sm" id="comment" name="comment"
                       maxlength="255" v-model="comment" placeholder="ex. accord cadre 2026">
            </div>
        </div>

    </div>
</template>

<script>
    import Swal from 'sweetalert2';

    export default {
        name: 'article-price-rule-form',
        props: ['oldRule', 'publicPrice', 'gridProfiles', 'gridEnabled', 'isScale'],
        data() {
            return {
                BASE_PUBLIC: 1,
                BASE_DISCOUNTED: 2,
                tierBase: 1,
                endsOn: '',
                comment: '',
                tiers: [],
                profiles: [],
            };
        },
        computed: {
            /**
             * Le détail par profil n'a de sens que si la remise du profil est maintenue :
             * ailleurs, le palier ne rencontre jamais la grille.
             */
            showsProfiles() {
                return this.tierBase == this.BASE_DISCOUNTED
                    && this.gridEnabled
                    && this.profiles.length > 0;
            },
            /** L'interrupteur pilote tier_base, qui reste ce que le formulaire enregistre. */
            cumulates: {
                get() {
                    return this.tierBase == this.BASE_DISCOUNTED && this.gridEnabled;
                },
                set(value) {
                    this.tierBase = value ? this.BASE_DISCOUNTED : this.BASE_PUBLIC;
                }
            },
            cumulHelp() {
                // Une idée par ligne : l'infobulle garde les retours à la ligne.
                return '• Activé : la remise du profil client s\'applique en plus, sur le prix remisé du palier.\n• Désactivé : seule la remise du palier s\'applique.';
            },
            /**
             * Réglage enregistré mais sans effet ici. Il n'est pas réécrit en douce : il
             * reprendra effet tel quel si la grille ou le profil reviennent.
             */
            cumulUnavailable() {
                return this.gridEnabled
                    ? 'Remise par profil maintenue, sans effet ici : l\'article n\'a pas de code de remise ou le client pas de profil.'
                    : 'Remise par profil maintenue, sans effet tant que la grille de remises est désactivée.';
            },
            beatenClass() {
                return this.isScale ? 'ix-tier-beaten' : 'ix-tier-unfavourable';
            },
            /** Autant de colonnes que l'en-tête, pour les lignes qui les traversent. */
            columnCount() {
                return this.showsProfiles ? 8 : 5;
            },
            duplicates() {
                const seen = {};
                const found = [];

                this.tiers.forEach(tier => {
                    const key = this.threshold(tier);
                    if (key === null) return;
                    if (seen[key] && found.indexOf(key) === -1) found.push(key);
                    seen[key] = true;
                });

                return found;
            },
            /** Le seuil le plus bas décide de ce qui se passe en dessous de lui. */
            firstThreshold() {
                const thresholds = this.tiers.map(t => this.threshold(t)).filter(t => t !== null);

                return thresholds.length ? Math.min.apply(null, thresholds) : 0;
            }
        },
        methods: {
            threshold(tier) {
                const value = parseFloat(tier.from_quantity);

                return isNaN(value) ? null : value;
            },
            public() {
                return parseFloat(this.publicPrice) || 0;
            },
            /** Le prix remisé du palier, au centime : public × (1 - taux). */
            discountedPrice(rate) {
                return Math.round(this.public() * (1 - rate / 100) * 100) / 100;
            },
            /** Le taux est la valeur enregistrée : le prix remisé le suit. */
            onRateInput(tier, value) {
                tier.value = value;

                const rate = parseFloat(value);
                tier.price = isNaN(rate) ? '' : this.discountedPrice(rate).toFixed(2);
            },
            /**
             * Le prix saisi devient un taux à trois décimales, celles de lines.discount. Le
             * champ n'est pas réécrit pendant la frappe, pour ne pas déplacer le curseur.
             */
            onPriceInput(tier, value) {
                tier.price = value;

                const price = parseFloat(value);
                const publicPrice = this.public();
                if (isNaN(price) || publicPrice <= 0) return;

                tier.value = String(Math.round((1 - price / publicPrice) * 100 * 1000) / 1000);
            },
            /** Au-delà d'environ 1 000 € de prix public, le taux arrondi peut décaler le prix d'un centime. */
            syncPrice(tier) {
                const rate = parseFloat(tier.value);
                tier.price = isNaN(rate) ? '' : this.discountedPrice(rate).toFixed(2);
            },
            /**
             * Deux remises se multiplient, elles ne s'additionnent pas : 15 % puis 3 %
             * font 17,55 % et non 18 %. Trois décimales, celles de lines.discount, pour
             * que l'écran affiche exactement le taux que le serveur écrira.
             *
             * Doit rester le miroir de SalePriceResolver::cumulate().
             */
            cumulatedRate(tier, profile) {
                const tierRate = parseFloat(tier.value);
                if (isNaN(tierRate)) return 0;

                // Une case absente de la grille n'est pas un taux de zéro : le palier joue seul.
                if (profile.rate === null) return tierRate;

                return Math.round(100 * (1 - (1 - profile.rate / 100) * (1 - tierRate / 100)) * 1000) / 1000;
            },
            profileNet(tier, profile) {
                return this.public() * (1 - this.cumulatedRate(tier, profile) / 100);
            },
            /**
             * Le prix que ce profil paierait sans la règle, uniquement avec sa remise de
             * grille. Une case absente de la grille, ou une société sans profil, ne change
             * rien au prix public.
             */
            profileGridNet(profile) {
                if (profile.rate === null) return this.public();

                return this.public() * (1 - profile.rate / 100);
            },
            /**
             * Un palier plus cher que la seule remise du profil. Règle par défaut : il n'est
             * pas appliqué, le client paie le meilleur prix. Règle client : il s'applique
             * quand même, mais on doit le voir.
             */
            isBeaten(net, profile) {
                if (profile.rate === null || profile.rate === undefined) return false;

                return Math.round(net * 100) > Math.round(this.profileGridNet(profile) * 100);
            },
            beatenTitle(profile) {
                const grid = ' (remise de profil seule : ' + this.currency(this.profileGridNet(profile)) + ')';

                return this.isScale
                    ? 'Non appliqué : la remise de profil seule est plus avantageuse' + grid
                    : 'Moins avantageux que la remise de profil, appliqué quand même' + grid;
            },
            beatenBy(tier) {
                const rate = parseFloat(tier.value);
                if (!this.gridEnabled || isNaN(rate)) return [];

                return this.profiles.filter(profile => this.isBeaten(this.discountedPrice(rate), profile));
            },
            beatenNote(tier) {
                // Règle client : un seul profil, inutile de le nommer.
                if (!this.isScale) return 'moins avantageux que la remise de profil';

                return 'non appliqué pour ' + this.beatenBy(tier).map(profile => profile.code).join(', ');
            },
            /** Une quantité sans décimales inutiles : « 10 unités » et non « 10,000 ». */
            quantity(value) {
                return String(value).replace('.', ',') + (value > 1 ? ' unités' : ' unité');
            },
            /** Le taux sans décimales inutiles : « 20 % » et non « 20,0000 % ». */
            rate(value) {
                return (value || 0).toFixed(3).replace(/\.?0+$/, '').replace('.', ',') + ' %';
            },
            currency(value) {
                return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);
            },
            isDuplicate(tier) {
                return this.duplicates.indexOf(this.threshold(tier)) !== -1;
            },
            addTier() {
                this.tiers.push({
                    uniq: Date.now() + Math.random(),
                    from_quantity: this.tiers.length ? '' : 1,
                    value: '',
                    price: '',
                    comment: '',
                });
            },
            removeTier(index) {
                const tier = this.tiers[index];

                // Un palier jamais enregistré s'enlève sans confirmation.
                if (!tier.id) {
                    this.tiers.splice(index, 1);
                    return;
                }

                Swal.fire({
                    title: 'Supprimer ce palier ?',
                    text: 'Les devis déjà établis ne sont pas recalculés.',
                    icon: 'warning',
                    showCancelButton: true,
                    cancelButtonText: 'Annuler',
                    confirmButtonColor: '#D15240',
                    confirmButtonText: 'Oui, supprimer',
                    reverseButtons: true,
                    heightAuto: false
                }).then((result) => {
                    if (result.value) this.tiers.splice(index, 1);
                });
            }
        },
        mounted() {
            this.profiles = JSON.parse(this.gridProfiles || '[]');

            const rule = JSON.parse(this.oldRule || 'null');

            // L'écran n'ouvre ce formulaire que sur une règle existante : la base par
            // défaut est posée à la création, par PriceRule::defaultTierBase().
            if (!rule) {
                this.addTier();
                return;
            }

            this.tierBase = parseInt(rule.tier_base, 10);
            this.endsOn = rule.ends_on || '';
            this.comment = rule.comment || '';

            this.tiers = (rule.tiers || []).map(tier => Object.assign({ price: '' }, tier, {
                uniq: Date.now() + Math.random(),
            }));
            this.tiers.forEach(tier => this.syncPrice(tier));

            if (!this.tiers.length) this.addTier();
        }
    }
</script>
